// ============================================================================
// QUICK-ADD PARSING — "dentist friday 3pm" → a day, a clock, and a shorter title.
//
// PURE, DETERMINISTIC, AND NOT AI. Nothing here calls a model, reads a clock, or consults a
// locale: `today` is passed in, every rule is a table in this file, and the same draft parsed
// twice on two machines gives the same answer. That is a product rule, not a preference — the
// composer is allowed to FILL IN a picker the owner can see and correct, and it is never allowed
// to GUESS on his behalf (CLAUDE.md → "Mirror, not doctor"; "the app never fakes knowledge").
//
// WHAT THAT BUYS THE UI. Because the result carries the exact `spans` it consumed, the composer
// can show the owner what it took, out of his own words, BEFORE he presses Enter — and put every
// character back if he rejects it. A parser that could not say what it ate would have to be
// trusted, and nothing in this app asks to be trusted.
//
// WHAT IS DELIBERATELY *NOT* RECOGNISED, because a wrong date is worse than no date:
//   · a bare `3:30` — nothing here knows whether that is morning or afternoon, so it stays in
//     the title. Meridiem (`3:30pm`), a 24-hour hour (`15:30`), or a padded hour (`09:00`) are
//     unambiguous; a bare small hour is not.
//   · `#anything` that is not already the name of a list. Unknown tags stay in the title and no
//     list is ever created by typing.
//   · a draft that is nothing but a date. "friday" is a title; consuming it would leave a task
//     with no words in it, so nothing is consumed at all.
//   · durations, priorities, recurrence, people. They can be added when they have a field to
//     land in — this file will not invent one.
//
// This lives in packages/shared rather than apps/web because it is the same grammar the capture
// endpoint would need the day a phone share-sheet gets it, and two implementations of a date
// grammar are two chances to disagree about what "next friday" means.
// ============================================================================

import { addDays, toDate, toISODate, weekdayOf } from './dates.js'
import type { ISODate } from './types.js'

/** What kind of thing a consumed run of characters turned into. */
export type QuickAddSpanKind = 'date' | 'time' | 'list'

/** A run of the draft the parser claimed, with its exact bounds so the UI can put it back. */
export interface QuickAddSpan {
  kind: QuickAddSpanKind
  /** Index of the first character consumed, in the ORIGINAL draft. */
  start: number
  /** Index one past the last character consumed. */
  end: number
  /** `draft.slice(start, end)` — carried so a caller never has to re-slice to display it. */
  text: string
}

export interface QuickAddParse {
  /** The draft with every span removed and the leftover whitespace collapsed. */
  title: string
  /** The calendar day named, or null. Never today-by-default; absent means absent. */
  due: ISODate | null
  /** Minutes past midnight, local wall clock, or null. 12-hour display is the caller's job. */
  minutes: number | null
  /** An EXISTING list name, matched case-insensitively and returned in its own casing. */
  list: string | null
  /** Everything consumed, in the order it appears in the draft. Empty ⇒ nothing was parsed. */
  spans: QuickAddSpan[]
}

/** True when the parse found anything at all — the UI's "should there be a chip" test. */
export function hasQuickAddParse(parse: QuickAddParse): boolean {
  return parse.spans.length > 0
}

// ── The grammar ─────────────────────────────────────────────────────────────
//
// Every table below is matched case-insensitively and only on whole words. Abbreviations are the
// ones a person actually types; `tues`/`thur`/`thurs` are in because they are typed, `t` is not
// because it is ambiguous between two days and this file does not flip coins.

const WEEKDAYS: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3, weds: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
}

const MONTHS: Record<string, number> = {
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,
}

/** Own-property lookup only — `'constructor'` is not a weekday. */
function lookup(table: Record<string, number>, word: string | undefined): number | null {
  if (word === undefined) return null
  const key = word.toLowerCase()
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key]! : null
}

const WEEKDAY_WORDS = Object.keys(WEEKDAYS).sort((a, b) => b.length - a.length).join('|')
const MONTH_WORDS = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join('|')

/**
 * Prepositions swallowed along with the thing they introduce, so "email advisor on friday" does
 * not leave the title reading "email advisor on". `due` is NOT in this list: "essay due" is a
 * title a person meant to write, and cutting it would be the parser editing his prose.
 */
const DATE_LEAD = '(?:\\b(?:on|by)\\s+)?'
const TIME_LEAD = '(?:\\b(?:at)\\s+|@\\s*)?'

interface Rule {
  re: RegExp
  /** Null ⇒ the shape matched but the calendar refuses it (`feb 31`), so it stays in the title. */
  read: (m: RegExpExecArray, today: ISODate) => number | ISODate | null
}

const DATE_RULES: Rule[] = [
  {
    // next friday · this friday
    re: new RegExp(`${DATE_LEAD}\\b(next|this)\\s+(${WEEKDAY_WORDS})\\b`, 'i'),
    read: (m, today) => {
      const wd = lookup(WEEKDAYS, m[2])
      if (wd === null) return null
      const base = nextWeekday(today, wd)
      return m[1]!.toLowerCase() === 'next' ? addDays(base, 7) : base
    },
  },
  {
    // today · tomorrow
    re: new RegExp(`${DATE_LEAD}\\b(today|tomorrow)\\b`, 'i'),
    read: (m, today) => (m[1]!.toLowerCase() === 'today' ? today : addDays(today, 1)),
  },
  {
    // friday · fri
    re: new RegExp(`${DATE_LEAD}\\b(${WEEKDAY_WORDS})\\b`, 'i'),
    read: (m, today) => {
      const wd = lookup(WEEKDAYS, m[1])
      return wd === null ? null : nextWeekday(today, wd)
    },
  },
  {
    // oct 12 · october 12th · oct. 12
    re: new RegExp(`${DATE_LEAD}\\b(${MONTH_WORDS})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'i'),
    read: (m, today) => forwardMonthDay(today, lookup(MONTHS, m[1]), Number(m[2])),
  },
  {
    // 12 oct · 12th october
    re: new RegExp(`${DATE_LEAD}\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_WORDS})\\.?\\b`, 'i'),
    read: (m, today) => forwardMonthDay(today, lookup(MONTHS, m[2]), Number(m[1])),
  },
  {
    // 10/12 · 10/12/26 · 10/12/2026 — month/day, the owner's own convention.
    re: new RegExp(`${DATE_LEAD}\\b(\\d{1,2})/(\\d{1,2})(?:/(\\d{2,4}))?\\b`, 'i'),
    read: (m, today) => {
      const month = Number(m[1])
      const day = Number(m[2])
      if (m[3] === undefined) return forwardMonthDay(today, month, day)
      const raw = Number(m[3])
      return exact(raw < 100 ? 2000 + raw : raw, month, day)
    },
  },
  {
    // 12th — that day of this month, or of the next month that has one.
    re: new RegExp(`${DATE_LEAD}\\b(\\d{1,2})(?:st|nd|rd|th)\\b`, 'i'),
    read: (m, today) => forwardDayOfMonth(today, Number(m[1])),
  },
]

const TIME_RULES: Rule[] = [
  {
    // 3pm · 3 pm · 3:30pm · at 3pm · @3pm
    re: new RegExp(`${TIME_LEAD}\\b(\\d{1,2})(?::(\\d{2}))?\\s*(am|pm)\\b`, 'i'),
    read: (m) => {
      const hour = Number(m[1])
      const minute = m[2] === undefined ? 0 : Number(m[2])
      if (hour < 1 || hour > 12 || minute > 59) return null
      const h = m[3]!.toLowerCase() === 'pm' ? (hour % 12) + 12 : hour % 12
      return h * 60 + minute
    },
  },
  {
    // 15:30 · 09:00 · 00:15 — 24-hour only. A bare `3:30` is NOT a time here: nothing in this
    // file knows whether it is morning or afternoon, and a guessed half-day is a missed thing.
    re: new RegExp(`${TIME_LEAD}\\b(\\d{1,2}):(\\d{2})\\b`, 'i'),
    read: (m) => {
      const hour = Number(m[1])
      const minute = Number(m[2])
      if (hour > 23 || minute > 59) return null
      const unambiguous = hour >= 13 || hour === 0 || m[1]!.length === 2
      return unambiguous ? hour * 60 + minute : null
    },
  },
]

interface Hit {
  start: number
  end: number
  value: number | ISODate
}

/**
 * The best reading of one family: the LONGEST match wins, earliest on a tie. Longest-first is
 * what makes "next friday" beat "friday" and "oct 12" beat the "sat" hiding inside "saturday
 * lunch oct 12" — without a hand-ordered precedence list that a later rule would silently break.
 */
function bestHit(rules: Rule[], draft: string, today: ISODate): Hit | null {
  const hits: Hit[] = []
  for (const { re, read } of rules) {
    const m = re.exec(draft)
    if (!m || m.index === undefined) continue
    const value = read(m, today)
    if (value === null) continue
    const [start, end] = trimmedBounds(draft, m.index, m.index + m[0].length)
    if (end > start) hits.push({ start, end, value })
  }
  if (hits.length === 0) return null
  hits.sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start)
  return hits[0]!
}

/**
 * Read a draft. `today` anchors every relative day; `lists` is the set of names a `#tag` may
 * match — pass none and `#tags` are simply left alone, which is the honest behaviour on a
 * surface that does not know what lists exist.
 */
export function parseQuickAdd(
  draft: string,
  { today, lists = [] }: { today: ISODate; lists?: readonly string[] },
): QuickAddParse {
  const nothing: QuickAddParse = {
    title: draft.trim(),
    due: null,
    minutes: null,
    list: null,
    spans: [],
  }
  if (!draft.trim()) return nothing

  let due: ISODate | null = null
  let minutes: number | null = null
  let list: string | null = null
  const spans: QuickAddSpan[] = []
  const claimed: Array<[number, number]> = []
  const overlaps = (s: number, e: number) => claimed.some(([a, b]) => s < b && e > a)
  const claim = (kind: QuickAddSpanKind, start: number, end: number) => {
    claimed.push([start, end])
    spans.push({ kind, start, end, text: draft.slice(start, end) })
  }

  const dateHit = bestHit(DATE_RULES, draft, today)
  if (dateHit) {
    due = dateHit.value as ISODate
    claim('date', dateHit.start, dateHit.end)
  }

  // The clock is read from what the day did not take, so "10/12" can never also be a time.
  const timeHit = bestHit(TIME_RULES, draft, today)
  if (timeHit && !overlaps(timeHit.start, timeHit.end)) {
    minutes = timeHit.value as number
    claim('time', timeHit.start, timeHit.end)
  }

  // An unknown tag is left exactly where it was typed. Nothing here creates a list.
  const tag = /(^|\s)#([\p{L}\p{N}_-]+)/u.exec(draft)
  if (tag && tag.index !== undefined) {
    const name = tag[2]!
    const known = lists.find((l) => l.toLowerCase() === name.toLowerCase())
    const start = tag.index + tag[1]!.length
    const end = start + name.length + 1
    if (known !== undefined && !overlaps(start, end)) {
      list = known
      claim('list', start, end)
    }
  }

  if (spans.length === 0) return nothing

  spans.sort((a, b) => a.start - b.start)
  const title = removeSpans(draft, spans)

  // A DRAFT THAT IS ONLY A DATE IS A TITLE. Consuming everything would submit a row with no
  // words in it, so the parse is abandoned whole rather than half-applied.
  if (!title) return nothing

  return { title, due, minutes, list, spans }
}

/**
 * The title left when only SOME of the spans are taken — for a surface that reads a token it
 * cannot store. The board's quick-add has no field meaning "3pm", so it keeps the time in the
 * words rather than deleting a phrase it is not going to save anywhere. Falls back to the whole
 * draft if the subset would empty the title, for the same reason `parseQuickAdd` does.
 */
export function quickAddTitle(draft: string, spans: readonly QuickAddSpan[]): string {
  const ordered = [...spans].sort((a, b) => a.start - b.start)
  return removeSpans(draft, ordered) || draft.trim()
}

/** Drop the spans and collapse the whitespace they leave behind. */
function removeSpans(draft: string, spans: ReadonlyArray<{ start: number; end: number }>): string {
  let out = ''
  let cursor = 0
  for (const s of spans) {
    out += draft.slice(cursor, s.start)
    cursor = s.end
  }
  out += draft.slice(cursor)
  return out.replace(/\s+/g, ' ').trim()
}

/** A match's bounds with any leading/trailing whitespace handed back to the title. */
function trimmedBounds(draft: string, start: number, end: number): [number, number] {
  let s = start
  let e = end
  while (s < e && /\s/.test(draft[s]!)) s++
  while (e > s && /\s/.test(draft[e - 1]!)) e--
  return [s, e]
}

/**
 * FORWARD, ALWAYS, AND SAID OUT LOUD:
 *   · a bare weekday is the NEXT one, counting today as day 0 — say "friday" on a Friday and it
 *     means today, because the day has not gone yet.
 *   · `next friday` is exactly a week past that. One rule, no calendar-week arithmetic to argue
 *     about, and the chip prints the resolved date anyway.
 *   · `oct 12`, `10/12` and `12th` roll to the following year / month once the day has passed.
 * Nothing here ever resolves backwards: a quick-add is about what is coming.
 */
function nextWeekday(today: ISODate, weekday: number): ISODate {
  return addDays(today, (weekday - weekdayOf(today) + 7) % 7)
}

/** The next occurrence of month/day, this year or next. Invalid days (`feb 31`) are refused. */
function forwardMonthDay(today: ISODate, month: number | null, day: number): ISODate | null {
  if (month === null) return null
  const year = toDate(today).getUTCFullYear()
  const thisYear = exact(year, month, day)
  if (!thisYear) return null
  return thisYear >= today ? thisYear : exact(year + 1, month, day)
}

/** The next occurrence of a day-of-month, this month or the next month that has such a day. */
function forwardDayOfMonth(today: ISODate, day: number): ISODate | null {
  const d = toDate(today)
  for (let i = 0; i < 13; i++) {
    const probe = exact(d.getUTCFullYear(), d.getUTCMonth() + 1 + i, day)
    if (probe && probe >= today) return probe
  }
  return null
}

/** Build a day, refusing one the calendar does not have (so `feb 31` parses as nothing). */
function exact(year: number, month: number, day: number): ISODate | null {
  if (day < 1 || day > 31 || month < 1) return null
  const d = new Date(Date.UTC(year, month - 1, day, 12))
  if (d.getUTCDate() !== day) return null
  return toISODate(d)
}
